import type { KeyboardEvent, MouseEvent, ReactElement } from "react";
import { BODY_TYPE } from "odyssey-shared";
import { Glyph, centreOffset, logical } from "../../ui/kit/index.ts";
import type { ActionId } from "../controls/actions.ts";
import { captureKey, type RefusedReason } from "../controls/capture.ts";
import type { SettingId } from "./settings-model.ts";
import { noteId } from "./settings-panes.tsx";
import { SETTINGS_WINDOW } from "./settings-sections.ts";
import { say } from "../../words.ts";

/**
 * Controls' key cap and a row's note (P1.9-T50; ADR-061's amendment "key rebinding", decisions 6 to 8; the design's
 * `Key` in `SettingsSurfaces.jsx`, a visual reference). A cap is a button: pressing it waits for a key, which the cap
 * takes before the input router and the modal's focus trap see it, so Tab is taken as a key and Escape only cancels
 * the wait.
 */

/**
 * A key cap's size as the design draws it, in logical pixels: its height, its least width, the padding either side
 * of its label, the gap between a row's Reset and its cap, and the gap between two caps of one row.
 */
export const KEY_CAP = { height: 12, minWidth: 28, paddingX: 3, resetGap: 4, capGap: 2 } as const;

/**
 * How a cap is drawn: at rest, waiting for a key, or in a clash.
 */
export type CapState = "rest" | "wait" | "clash";

/**
 * One line of a row's note, with a key that changes when it is to be said again.
 */
export interface NoteLine {
  readonly id: string;
  readonly text: string;
}

/**
 * Keeps a press on a cap from doing what the browser would: the cap takes focus by its click handler, on every
 * browser alike.
 *
 * @param event The press.
 */
function keepPress(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * An action's key cap: its label at rest or in a clash, "Press a key" while it waits.
 *
 * @param props The `action`; its key's `label`; its `state`; its accessible `name`; the id of the note that
 *   describes it, if any (`describedBy`); and `onWait`, `onRefuse`, `onChoose` and `onDone`, which start the wait,
 *   record a refused key, take a key and end the wait.
 * @returns The cap.
 */
export function KeyCap({ action, label, state, name, describedBy, onWait, onRefuse, onChoose, onDone }: { readonly action: ActionId; readonly label: string; readonly state: CapState; readonly name: string; readonly describedBy: string | undefined; readonly onWait: () => void; readonly onRefuse: (key: string, reason: RefusedReason) => void; readonly onChoose: (code: string) => void; readonly onDone: () => void }): ReactElement {
  const waits = state === "wait";

  /**
   * A key press on the cap. While it waits every press is the cap's alone: Escape cancels, a key that cannot be bound
   * is refused and any other, a modifier alone among them, is chosen by its physical key; an auto-repeat does nothing. At rest
   * Enter and Space press the cap as they press any button and go no further.
   *
   * @param event The press.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (!waits) {
      if (event.key === "Enter" || event.key === " ") {
        event.stopPropagation();
        if (event.repeat) {
          event.preventDefault();
        }
      }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.repeat) {
      return;
    }
    const capture = captureKey(event, action);
    if (capture.kind === "cancel") {
      onDone();
    } else if (capture.kind === "refused") {
      onRefuse(capture.code, capture.reason);
    } else if (capture.kind === "key") {
      onChoose(capture.code);
    }
  };

  /**
   * A key released on the cap: while it waits the release does nothing, so Space coming up never presses the cap
   * again; at rest Enter and Space go no further than the cap.
   *
   * @param event The release.
   */
  const onKeyUp = (event: KeyboardEvent<HTMLButtonElement>): void => {
    if (waits) {
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === "Enter" || event.key === " ") {
      event.stopPropagation();
    }
  };

  return (
    <button
      type="button"
      className={`ot-game-settings__cap is-${state}`}
      data-control={action}
      data-state={state}
      aria-label={name}
      aria-describedby={describedBy}
      onMouseDown={keepPress}
      onClick={(event) => {
        event.currentTarget.focus();
        onWait();
      }}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={waits ? onDone : undefined}
      style={{ minWidth: logical(KEY_CAP.minWidth), height: logical(KEY_CAP.height), padding: `0 ${logical(KEY_CAP.paddingX)}` }}
    >
      {waits ? say("controls.pressKey") : label}
    </button>
  );
}

/**
 * A row's note, under its row: a polite live region that stays in the pane while it holds nothing, with no height
 * and the pane's gap taken back, as a setting's note is. A clash's note is in the warning colour behind the alert
 * glyph, as the design draws it. A line is a new element whenever its key changes, so the same refusal twice is said
 * twice.
 *
 * @param props The row's setting `id`, the note's `lines` and whether it warns.
 * @returns The region.
 */
export function KeyNote({ id, lines, warning }: { readonly id: SettingId; readonly lines: readonly NoteLine[]; readonly warning: boolean }): ReactElement {
  if (lines.length === 0) {
    return <div id={noteId(id)} className="ot-game-settings__note" aria-live="polite" data-note-for={id} style={{ marginTop: logical(-SETTINGS_WINDOW.rowGap) }} />;
  }
  return (
    <div id={noteId(id)} className={`ot-game-settings__note ot-game-settings__key-note${warning ? " is-warning" : ""}`} aria-live="polite" data-note-for={id} data-game-menu="note" style={{ gap: logical(SETTINGS_WINDOW.controlGap) }}>
      {warning ? (
        <span className="ot-game-settings__part" style={{ marginTop: logical(centreOffset(BODY_TYPE.body.lineHeight, 8)) }}>
          <Glyph name="alert" />
        </span>
      ) : null}
      <span className="ot-game-settings__label">
        {lines.map((line) => (
          <span key={line.id} className="ot-game-settings__line">
            {line.text}
          </span>
        ))}
      </span>
    </div>
  );
}
